import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Checkbox, Flex, HStack, Popover, PopoverBody, PopoverContent, PopoverTrigger, Spacer, Text, useToast } from '@chakra-ui/react'
import { RangeDatepicker } from '../../../../range'
import { useTranslation } from 'react-i18next'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { EventTable } from '../../../../components/EventTable/EventTable'
import { useParams } from 'react-router-dom'
import { DashboardProfile, UserDetailsResponse } from '../../../../services/network'
import { log } from '../../../../utils/logger'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../store/store'
import { createExportApi } from '../../../../utils/api-interface'
import dayjs from 'dayjs'
import { json2csv } from 'json-2-csv'
import { getErrorMessage } from '../../../../utils/utils'
import {
  EventStatusFilter,
  eventStatusFilters,
  getEventSearchFilters,
  getEventStatusFilterLabelKey,
  getEventStatusLabelKey
} from '../../../../utils/event-status'

export interface IUserEventsProps {
  user: UserDetailsResponse | undefined
}

function UserEvents(): JSX.Element {
  const { t } = useTranslation()
  const toast = useToast()

  const [selectedDates, setSelectedDates] = useState<Date[]>([])
  const [selectedStatuses, setSelectedStatuses] = useState<EventStatusFilter[]>([])
  const { id } = useParams<{ id: string }>()

  const dashboardUser: DashboardProfile | null = useSelector((state: RootState) => state.dashboardUser.dashboardUser)
  const canUpdate = useMemo(() => {
    if (!dashboardUser) {
      return false
    }

    if (!dashboardUser.role || !dashboardUser.role.title) {
      return false
    }

    return dashboardUser.role.title === process.env.REACT_APP_ROLE_ADMINISTRATOR || dashboardUser.role.title === process.env.REACT_APP_ROLE_BACKOFFICE
  }, [dashboardUser])

  const handleCheckboxChange = (status: EventStatusFilter): void => {
    setSelectedStatuses((prevSelectedStatuses) => {
      if (prevSelectedStatuses.includes(status)) {
        return prevSelectedStatuses.filter((s) => s !== status)
      } else {
        return [...prevSelectedStatuses, status]
      }
    })
  }

  const removeStatus = (): void => {
    setSelectedStatuses([])
  }

  const removeSelectedDates = (): void => {
    setSelectedDates([])
  }

  useEffect(() => {
    eva.replace()
  }, [])

  const dateFilterString = useMemo((): string => {
    if (selectedDates.length <= 1) {
      return ''
    }
    const firstDay = selectedDates[0]
    const lastDay = selectedDates[1]
    const lastPart = `${selectedDates[1].getDate()} ${selectedDates[1].toLocaleString('it-IT', { month: 'short' })} ${selectedDates[1].getFullYear()}`
    if (firstDay.getFullYear() !== lastDay.getFullYear() || firstDay.getMonth() !== lastDay.getMonth()) {
      const firstPart = `${selectedDates[0].getDate()} ${selectedDates[0].toLocaleString('it-IT', { month: 'short' })} ${selectedDates[0].getFullYear()}`
      return `${firstPart} - ${lastPart}`
    } else {
      return `${selectedDates[0].getDate()} - ${lastPart}`
    }
  }, [selectedDates])

  const exportEvent = useCallback(async () => {
    if (!id) {
      // toast
      return
    }
    let startDate = dayjs().subtract(30, 'days').format('YYYY-MM-DD')
    let endDate = dayjs().format('YYYY-MM-DD')
    if (selectedDates.length === 2) {
      startDate = dayjs(selectedDates[0]).format('YYYY-MM-DD')
      endDate = dayjs(selectedDates[1]).format('YYYY-MM-DD')
    } else {
      log.debug(`[UserEvents] Downloading the events in the last month`)
    }

    log.debug(`[UserEvents] Downloading the events from ${startDate} to ${endDate}`)

    try {
      const exportApi = createExportApi()
      const filters = getEventSearchFilters(selectedStatuses)
      const createdResponse = await exportApi.exportUserEventsCreated(id, startDate, endDate, filters.status, filters.noShowCheckIns)
      const reservedResponse = await exportApi.exportUserEventsReserved(id, startDate, endDate, filters.status, filters.noShowCheckIns)

      let events = [...createdResponse.data.events, ...reservedResponse.data.events]
      events = events.sort((e1, e2) => e1.eventDate.localeCompare(e2.eventDate))
      const mappedEvents = events.map((e) => ({
        eventId: e.eventId,
        eventName: e.eventName,
        eventStatus: t(getEventStatusLabelKey(e.eventStatus, e.successfulCheckInsCount)),
        successfulCheckInsCount: e.successfulCheckInsCount ?? null,
        description: e.description,
        eventDate: dayjs(e.eventDate).format(`YYYY-MM-DD HH:mm:ss`),
        eventDuration: `${((Number(e.eventDuration) * 60) / 60).toFixed(0)}h ${(Number(e.eventDuration) * 60) % 60}m`,
        locationName: e.locationName,
        address: e.address,
        city: e.city,
        lat: e.lat,
        lon: e.lon,
        isPrivate: e.isPrivate,
        isOutdoor: e.isOutdoor,
        isPaid: e.isPaid,
        payment: e.payment?.amountSee ? `${(Math.ceil(e.payment?.amountSee) / 100).toFixed(2)} ${e.payment?.currency}` : '0.00 Eur',
        organizer: e.organizer,
        activity: e.activity,
        hasManualParticipantsApproval: e.hasManualParticipantsApproval,
        participantCount: e.participantCount,
        areParticipantsLimited: e.areParticipantsLimited,
        minParticipants: e.minParticipants,
        maxParticipants: e.maxParticipants,
        participants: e.participants.map((p) => ({
          ...p,
          status: t(`participant.${p.status}`),
          cancelReason: p.cancelReason ? t(`participant.cancelReason.${p.cancelReason}`) : null
        })),
        eventImageUrl: e.eventImageUrl
      }))

      log.debug(
        `[UserEvents] events ${JSON.stringify(
          events.map((e) => e.payment),
          null,
          ' '
        )}`
      )

      const csvContent = json2csv(mappedEvents)
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)

      // Crea e attiva il download
      const a = document.createElement('a')
      a.href = url
      a.download = `${dayjs().format('YYYYMMDD')}_user_${id}_events.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast({
        title: t('csv_success'),
        status: 'success',
        duration: 4000,
        isClosable: true
      })
    } catch (error) {
      log.error('Errore nella conversione JSON a CSV', error)
      toast({
        title: t('csv_error'),
        description: getErrorMessage(error),
        status: 'error',
        duration: 4000,
        isClosable: true
      })
    }
  }, [id, selectedDates, selectedStatuses, t, toast])

  return (
    <Flex flexDirection={'column'}>
      <HStack>
        {selectedStatuses.length > 0 ? (
          <HStack>
            <Text
              bg={'primary.400'}
              border={'1px solid primary.400'}
              borderRadius='600px'
              alignItems={'center'}
              padding={'8px 10px'}
              gap='2px'
              color={'NeutralLight'}
              fontWeight='600'
              fontSize='14px'
              lineHeight='19.6px'>
              {t(getEventStatusFilterLabelKey(selectedStatuses[0]))} {selectedStatuses.length > 1 ? `(+${selectedStatuses.length - 1})` : ''}
              <Button alignItems={'center'} size='xs' onClick={(): void => removeStatus()}>
                <Text
                  bg={'primary.400'}
                  border={'1px solid primary.400'}
                  borderRadius='600px'
                  alignItems={'center'}
                  padding={'2px 0px'}
                  color={'NeutralLight'}
                  fontWeight='600'
                  fontSize='14px'
                  lineHeight='19.6px'>
                  X
                </Text>
              </Button>
            </Text>
          </HStack>
        ) : (
          <></>
        )}
        {selectedDates.length > 1 ? (
          <Text
            bg={'primary.400'}
            border={'1px solid primary.400'}
            borderRadius='600px'
            alignItems={'center'}
            padding={'8px 10px'}
            gap='2px'
            color={'NeutralLight'}
            fontWeight='600'
            fontSize='14px'
            lineHeight='19.6px'>
            {dateFilterString}
            <Button alignItems={'center'} size='xs' onClick={(): void => removeSelectedDates()}>
              <Text
                bg={'primary.400'}
                border={'1px solid primary.400'}
                borderRadius='600px'
                alignItems={'center'}
                padding={'2px 0px'}
                color={'NeutralLight'}
                fontWeight='600'
                fontSize='14px'
                lineHeight='19.6px'>
                X
              </Text>
            </Button>
          </Text>
        ) : (
          <></>
        )}
        <Popover placement='bottom-start'>
          <PopoverTrigger>
            <Button
              borderRadius='600px'
              padding={'8px, 10px, 8px, 10px'}
              gap='2px'
              bg='greyWarm100'
              border={'5px solid primary.400'}
              fontWeight='600'
              fontSize='14px'
              lineHeight='19.6px'
              textTransform='capitalize'>
              {t('status')}
              <i data-eva={'chevron-down'}></i>
            </Button>
          </PopoverTrigger>
          <PopoverContent borderRadius={'26px'}>
            <PopoverBody display={'flex'} flexDirection='column' gap={'26px'} padding={'20px'}>
              {eventStatusFilters.map((s) => (
                <Checkbox key={s} variant={'primary'} value={s} isChecked={selectedStatuses.includes(s)} onChange={(): void => handleCheckboxChange(s)}>
                  {t(getEventStatusFilterLabelKey(s))}
                </Checkbox>
              ))}
            </PopoverBody>
          </PopoverContent>
        </Popover>
        <RangeDatepicker buttonText='event_date' selectedDates={selectedDates} onDateChange={setSelectedDates}></RangeDatepicker>
        <Spacer></Spacer>
        {canUpdate && (
          <Button variant='outline' key={'export_events_csv'} onClick={exportEvent}>
            <i data-eva={'download-outline'} data-eva-fill={'#353F48'}></i>
            {t('users.user_event.export_event_csv')}
          </Button>
        )}
      </HStack>
      {id && (
        <EventTable
          eventType={'created'}
          userId={id}
          startDate={selectedDates[0] ? dayjs(selectedDates[0]).format(`YYYY-MM-DD`) : ''}
          endDate={selectedDates[1] ? dayjs(selectedDates[1]).format(`YYYY-MM-DD`) : ''}
          selectedStatuses={selectedStatuses}
        />
      )}
      {id && (
        <EventTable
          eventType={'partecipated'}
          userId={id}
          startDate={selectedDates[0] ? dayjs(selectedDates[0]).format(`YYYY-MM-DD`) : ''}
          endDate={selectedDates[1] ? dayjs(selectedDates[1]).format(`YYYY-MM-DD`) : ''}
          selectedStatuses={selectedStatuses}
        />
      )}
    </Flex>
  )
}

export { UserEvents }
